import type { AutomergeUrl } from "@automerge/automerge-repo";
import { useDocHandle } from "@automerge/react";
import type { Monaco } from "@monaco-editor/react";
import {
  MonacoEditor,
  type EditorSelection,
} from "forest/monaco-editor/MonacoEditor";
import type { EditorThemes } from "forest/monaco-editor/shiki-themes";
import type { editor } from "monaco-editor";
import { findLastCheckpointHeads } from "playground/vcs";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";

import { useTextComments } from "@/comments/text-comments";
import { useBearSandbox } from "@/components/BearSandboxProvider";
import { useIsMac } from "@/hooks/use-is-mac";
import { useWorkspacePane } from "@/panes/Workspace";

import { useEditorSettings } from "../lib/editor-settings";
import { configureMonacoTypeScript } from "../lib/monaco-setup";

// Cool-toned to sit with the studio's blue steel chrome.
const THEMES: EditorThemes = {
  light: "github-light-default",
  dark: "github-dark-default",
};

function headsKey(handle: { heads(): string[] | undefined }): string {
  return handle.heads()?.join(",") ?? "";
}

/**
 * Tracks dirty state for an Automerge document.
 *
 * Dirty = current heads differ from the last checkpoint's heads.
 * Automatically clears when a new checkpoint (local or remote) is detected.
 */
function useDirty(url: AutomergeUrl) {
  const handle = useDocHandle<{ text: string }>(url, { suspense: true });
  const [dirty, setDirty] = useState(false);
  // The heads key we consider "clean". Either the last checkpoint or the
  // heads at mount time (so a freshly-loaded doc starts clean).
  const baselineRef = useRef<string>("");

  useEffect(() => {
    if (!handle) return;

    // Seed baseline: last checkpoint if one exists, otherwise current heads
    const lastCheckpoint = findLastCheckpointHeads(handle);
    baselineRef.current = lastCheckpoint?.join(",") ?? headsKey(handle);
    // Track the last-seen checkpoint so we detect new ones (including remote)
    let lastSeenCheckpoint: string | null = lastCheckpoint?.join(",") ?? null;

    const onHeadsChanged = () => {
      const current = headsKey(handle);

      // Detect any new checkpoint (local or remote) by comparing against
      // the last-seen checkpoint heads, rather than only checking the very
      // last history entry.
      const cp = findLastCheckpointHeads(handle);
      const cpKey = cp?.join(",") ?? null;
      if (cpKey && cpKey !== lastSeenCheckpoint) {
        lastSeenCheckpoint = cpKey;
        baselineRef.current = cpKey;
      }

      setDirty(current !== baselineRef.current);
    };

    onHeadsChanged();
    handle.on("heads-changed", onHeadsChanged);
    return () => {
      handle.off("heads-changed", onHeadsChanged);
    };
  }, [handle]);

  return dirty;
}

type SourcePaneProps = {
  paneId: string;
  filename: string;
  url?: AutomergeUrl;
  onDirtyChange?: (dirty: boolean) => void;
  /** Open another workspace file, e.g. a "go to definition" target. */
  onOpenPath?: (path: string, selection?: EditorSelection) => void;
  /** Where to put the cursor once this file loads (a jump landed here). */
  revealSelection?: EditorSelection;
  /** A comment thread to open (the `show` tool, a chat link). */
  revealThread?: { threadId: string };
};

const SourcePane = ({
  paneId,
  filename,
  url,
  onDirtyChange,
  onOpenPath,
  revealSelection,
  revealThread,
}: SourcePaneProps) => {
  const { focusPane, isFocused } = useWorkspacePane(paneId);

  const handlePointerDownCapture = useCallback(() => {
    focusPane();
  }, [focusPane]);

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.stopPropagation();
    },
    [],
  );

  // No Automerge doc behind this path. That is either a file the project
  // doesn't have, or one it doesn't own: mounted library content lives in a
  // published unit, not in the doc. Ask the filesystem which, since it is the
  // only thing that can see across a mount.
  const { playground } = useBearSandbox();
  const [managed, setManaged] = useState<"checking" | "found" | "missing">(
    "checking",
  );
  useEffect(() => {
    if (url) return;
    let cancelled = false;
    setManaged("checking");
    void playground
      .resolve(filename)
      .then((resolved) => {
        if (!cancelled) setManaged(resolved ? "found" : "missing");
      })
      .catch(() => {
        if (!cancelled) setManaged("missing");
      });
    return () => {
      cancelled = true;
    };
  }, [playground, filename, url]);

  if (!url && managed !== "found") {
    return (
      <div className="p-4 h-full flex items-center justify-center text-sm text-muted-foreground">
        {managed === "missing" ? `Source file "${filename}" not found.` : null}
      </div>
    );
  }

  return (
    <div
      className="h-full"
      data-workspace-pane
      onPointerDownCapture={handlePointerDownCapture}
      onPointerDown={handlePointerDown}
    >
      {url ? (
        <LinkedSourceEditor
          url={url}
          filename={filename}
          isFocused={isFocused}
          onDirtyChange={onDirtyChange}
          onOpenPath={onOpenPath}
          revealSelection={revealSelection}
          revealThread={revealThread}
        />
      ) : (
        // Same editor, no Automerge sync and nothing to mark dirty: the
        // MonacoEditor opens a mounted file through the playground and turns
        // itself read-only, so library source reads exactly like project
        // source, syntax highlighting and all.
        <MonacoEditor
          playground={playground}
          path={filename}
          themes={THEMES}
          isFocused={isFocused}
          onOpenPath={onOpenPath}
          revealSelection={revealSelection}
          onConfigureMonaco={configureMonacoTypeScript}
        />
      )}
    </div>
  );
};

const LinkedSourceEditor = ({
  url,
  filename,
  isFocused,
  onDirtyChange,
  onOpenPath,
  revealSelection,
  revealThread,
}: {
  url: AutomergeUrl;
  filename: string;
  isFocused: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  onOpenPath?: (path: string, selection?: EditorSelection) => void;
  revealSelection?: EditorSelection;
  revealThread?: { threadId: string };
}) => {
  const dirty = useDirty(url);
  const comments = useTextComments(url, filename);
  const activate = comments.activate;
  const loaded = comments.source !== undefined;
  useEffect(() => {
    if (revealThread && loaded) activate(revealThread.threadId);
    // `activate` is a fresh closure each render; the reveal is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealThread, loaded]);
  const { playground } = useBearSandbox();
  const { vimMode } = useEditorSettings();
  const isMac = useIsMac();

  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;

  useEffect(() => {
    onDirtyChangeRef.current?.(dirty);
  }, [dirty]);

  // Bear-specific keybindings: prevent Cmd+S triggering the browser save
  // dialog (edits auto-sync), and let Cmd+K / Cmd+Enter bubble out so the
  // workspace command palette and Run-project shortcuts still fire while
  // the editor is focused.
  const onEditorMount = useCallback(
    (ed: editor.IStandaloneCodeEditor, monaco: Monaco) => {
      ed.addAction({
        id: "prevent-browser-save",
        label: "Prevent browser save",
        keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS],
        run: () => {},
      });
      ed.onKeyDown((e) => {
        const mod = isMac ? e.metaKey : e.ctrlKey;
        if (
          mod &&
          !e.shiftKey &&
          !e.altKey &&
          (e.code === "KeyK" || e.code === "Enter")
        ) {
          e.preventDefault();
          e.stopPropagation();
          const event = new KeyboardEvent("keydown", {
            key: e.browserEvent.key,
            code: e.browserEvent.code,
            metaKey: e.metaKey,
            ctrlKey: e.ctrlKey,
            shiftKey: e.shiftKey,
            altKey: e.altKey,
            bubbles: true,
            cancelable: true,
          });
          ed.getDomNode()?.parentElement?.dispatchEvent(event);
        }
      });
    },
    [isMac],
  );

  // Bear's TypeScript config (jsxImportSource, "@/*" mappings, etc.). Forest
  // sets sane defaults; we layer Bear's overrides on before mount.
  const onConfigure = useCallback((monaco: Monaco) => {
    configureMonacoTypeScript(monaco);
  }, []);

  // Memoize so MonacoEditor's effects don't re-run every render.
  const monacoProps = useMemo(
    () => ({
      onMount: onEditorMount,
      onConfigureMonaco: onConfigure,
    }),
    [onEditorMount, onConfigure],
  );

  return (
    <MonacoEditor
      onOpenPath={onOpenPath}
      revealSelection={revealSelection}
      playground={playground}
      path={filename}
      themes={THEMES}
      isFocused={isFocused}
      vimMode={vimMode}
      comments={{
        threads: comments.highlights,
        activeId: comments.activeId,
        onActivate: comments.activate,
        onComment: (selection) =>
          comments.startDraft({ ...selection, display: selection.quote }),
        card: comments.card,
      }}
      {...monacoProps}
    />
  );
};

export default SourcePane;
